import type {AttachedDocument} from '@domain/refractory-product-catalog'
import {useFile} from '@hconnect/common/hooks/useFile'
import {Avatar, Button, LabelSmallRegular, Loading, TextMedium} from '@hm/fundamental'
import {mdiFileDownloadOutline, mdiDeleteOutline} from '@mdi/js'
import {AttachmentHandler} from '@widgets/common-attachments'
import moment from 'moment'
import {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useDeleteRefractoryDocument} from '../../hooks/mutations'

interface RefraCatalogDocumentPreviewProps {
  refractoryId: number
  document: AttachedDocument
}

export const RefraCatalogDocumentPreview: React.FC<RefraCatalogDocumentPreviewProps> = ({
  refractoryId,
  document
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {mutate: deleteDocument, isPending: isDeletingDocument} = useDeleteRefractoryDocument()

  const isImage = document.mediaType.includes('image')
  const {data, isFetching} = useFile(document.url, document.mediaType, document.id)

  const source = useMemo(() => (data ? URL.createObjectURL(new Blob([data])) : undefined), [data])

  useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source)
    },
    [source]
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">
        {isFetching || !source ? (
          <Loading size="large" data-cy="attached_document_file_loader" />
        ) : (
          <div
            data-cy={
              isImage
                ? `attached_image_${document.fileName}`
                : `attached_document_${document.fileName}`
            }
          >
            <AttachmentHandler
              file={source}
              alt={document.fileName}
              iconOrPreview={isImage ? 'preview' : 'icon'}
              mediaType={document.mediaType}
              sx={{maxWidth: '800px', maxHeight: '70vh', objectFit: 'contain'}}
            />
          </div>
        )}
      </div>
      <div className="flex justify-between items-center gap-6">
        <div className="flex w-[335px] flex-col gap-1" data-cy="refra-document-info">
          <TextMedium className="break-words">{document.fileName}</TextMedium>
          <div className="flex items-center gap-1 text-foreground_faded">
            <LabelSmallRegular>{t('common.uploadedBy')}</LabelSmallRegular>
            <Avatar name={document.uploadedByName} size="small" />
            <LabelSmallRegular>
              {document.uploadedByName} {t('common.on')}{' '}
              {moment(document.uploadedOn).format('MM/DD/YYYY HH:mm')}
            </LabelSmallRegular>
          </div>
        </div>
        <div className="flex gap-4">
          <Button
            icon={mdiDeleteOutline}
            variant="tertiary"
            disabled={isDeletingDocument}
            className="text-red-500"
            onClick={() =>
              deleteDocument({
                refractoryId: refractoryId,
                documentId: document.id
              })
            }
            data-cy="refra-document-delete-button"
          >
            {t('common.delete')}
          </Button>
          <a href={source} download={document.fileName}>
            <Button
              icon={mdiFileDownloadOutline}
              variant="secondary"
              disabled={isDeletingDocument || !source}
              data-cy="refra-document-download-button"
            >
              {t('common.download')}
            </Button>
          </a>
        </div>
      </div>
    </div>
  )
}
